"use client"

import { useState, useEffect, useContext } from "react"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Badge } from "@/components/ui/badge"
import { useLocale } from "@/hooks/use-locale"
import { ShadowContext } from "./user-review-shadow-root"

declare global {
  interface Window {
    ictportal_user_reviews?: {
      locale?: string
      logged_in?: boolean
    }
    ictportal_user_experiences?: {
      locale?: string
    }
  }
}

const localeOptions = [
  { value: "nl", label: "🇳🇱 Nederlands", flag: "🇳🇱" },
  { value: "en", label: "🇬🇧 English", flag: "🇬🇧" },
  { value: "es", label: "🇪🇸 Español", flag: "🇪🇸" },
]

export function LocaleSelector() {
  const currentLocale = useLocale()
  const [isClient, setIsClient] = useState(false)
  const shadowRoot = useContext(ShadowContext) || undefined;
  // Only show in development mode
  const isDevelopment = process.env.NODE_ENV === "development"

  useEffect(() => {
    setIsClient(true)
  }, [])

  const handleLocaleChange = (newLocale: string) => {
    if (typeof window !== "undefined") {
      if (!window.ictportal_user_reviews) {
        window.ictportal_user_reviews = {}
      }
      if (!window.ictportal_user_experiences) {
        window.ictportal_user_experiences = {}
      }

      // Keep both keys in sync (PHP uses ictportal_user_reviews; legacy override uses experiences)
      window.ictportal_user_reviews.locale = newLocale
      window.ictportal_user_experiences.locale = newLocale

      window.dispatchEvent(
        new StorageEvent("storage", {
          key: "ictportal_locale_change",
          newValue: newLocale,
        }),
      )

      window.location.reload()
    }
  }

  // Don't render in production or on server
  if (!isDevelopment || !isClient) {
    return null
  }

  const currentOption = localeOptions.find((option) => option.value === currentLocale)

  return (
    <div className="fixed top-4 right-4 z-[110] flex items-center gap-2">
      <Badge variant="secondary" className="text-xs">
        🛠️ DEV
      </Badge>
      <div className="bg-white border border-gray-300 rounded-lg shadow-lg p-2">
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-600 font-medium">Locale:</span>
          <Select value={currentLocale} onValueChange={handleLocaleChange}>
            <SelectTrigger className="w-32 h-8 text-xs">
              <SelectValue>
                <div className="flex items-center gap-1">
                  <span>{currentOption?.flag}</span>
                  <span>{currentLocale.toUpperCase()}</span>
                </div>
              </SelectValue>
            </SelectTrigger>
            <SelectContent container={shadowRoot}>
              {localeOptions.map((option) => (
                <SelectItem key={option.value} value={option.value} className="text-xs">
                  <div className="flex items-center gap-2">
                    <span>{option.flag}</span>
                    <span>{option.label.split(" ")[1]}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  )
}
